import { useEffect, useState } from "react";
import { LEAD_CATEGORIES } from "../utils/displayFormatters";
import { AI_NEXT_ACTIONS } from "../utils/displayFormatters";

const LeadListSidebar = ({
  leadTypeData,
  leadStatusData,
  selectedStatus,
  selectedType,
  onFilterChange,
  selectedFollowUpStatus,
  onFollowUpFilterChange,
  selectedUnreadMessages,
  onUnreadMessagesChange,
  messagingEnabled = false,
  selectedPriority,
  onPriorityFilterChange,
  selectedNextAction,
  onNextActionFilterChange,
  selectedUrgentOnly,
  onUrgentOnlyChange,
  priorityCounts = { high_priority: 0, standard: 0, routine: 0, urgent: 0, no_analysis: 0 },
  selectedReadStatus,
  onReadStatusFilterChange,
  unreadLeadsCount = 0,
  unreadMessagesCount = 0,
  selectedNeedsCallback = false,
  needsCallbackCount = 0,
  waitingReplyCount = 0,
  onNeedsCallbackChange,
  selectedWaitingReply = false,
  onWaitingReplyChange,
  selectedCategory = "",
  onCategoryFilterChange,
  categoryCounts = {},
  followUpCounts = { past_due: 0, due_today: 0, due_this_week: 0, due_later: 0, no_follow_up: 0 },
  // "sheet": full width inside the Mobile view filters sheet, sections start collapsed.
  layout = "sidebar",
}) => {
  const isSheet = layout === "sheet";
  const [isPriorityOpen, setIsPriorityOpen] = useState(!isSheet);
  const [isStatusOpen, setIsStatusOpen] = useState(!isSheet);
  const [isCategoryOpen, setIsCategoryOpen] = useState(!isSheet);
  const [isMessagesOpen, setIsMessagesOpen] = useState(false);
  const [isFollowUpOpen, setIsFollowUpOpen] = useState(false);
  const [isTypeOpen, setIsTypeOpen] = useState(false);
  const [isReadStatusOpen, setIsReadStatusOpen] = useState(!isSheet);

  const FOLLOW_UP_STATUSES = [
    {
      key: "",
      label: "All Reminders",
      color: "bg-accent-teal-subtle text-primary",
    },
    {
      key: "past_due",
      label: "Past Due",
      color:
        "bg-status-no-show/10 text-status-no-show border border-status-no-show/20",
    },
    {
      key: "due_today",
      label: "Due Today",
      color:
        "bg-status-contacted/10 text-status-contacted border border-status-contacted/20",
    },
    {
      key: "due_this_week",
      label: "Due This Week",
      color:
        "bg-status-scheduled/10 text-status-scheduled border border-status-scheduled/20",
    },
    {
      key: "due_later",
      label: "Due Later",
      color:
        "bg-status-converted/10 text-status-converted border border-status-converted/20",
    },
    {
      key: "no_follow_up",
      label: "No Reminder",
      color:
        "bg-status-not-interested/10 text-status-not-interested border border-status-not-interested/20",
    },
  ];

  const PRIORITY_OPTIONS = [
    {
      key: "",
      label: "All Priorities",
      color: "bg-accent-teal-subtle text-primary",
    },
    {
      key: "3",
      label: "High Priority",
      color: "bg-red-100 text-red-800 border border-red-300",
      countKey: "high_priority",
    },
    {
      key: "2",
      label: "Standard",
      color: "bg-yellow-100 text-yellow-800 border border-yellow-300",
      countKey: "standard",
    },
    {
      key: "1",
      label: "Routine",
      color: "bg-blue-100 text-blue-800 border border-blue-300",
      countKey: "routine",
    },
  ];

  const READ_STATUS_OPTIONS = [
    {
      key: "",
      label: "All",
      color: "bg-accent-teal-subtle text-primary",
    },
    {
      key: "false",
      label: "Unread Only",
      color: "bg-blue-100 text-blue-800 border border-blue-300",
    },
    {
      key: "true",
      label: "Read Only",
      color: "bg-gray-100 text-gray-800 border border-border",
    },
  ];

  const getStatusColor = (key) => {
    const colors = {
      new: "bg-status-new-lead/10 text-status-new-lead border border-status-new-lead/20",
      contacted:
        "bg-status-contacted/10 text-status-contacted border border-status-contacted/20",
      qualified:
        "bg-status-converted/10 text-status-converted border border-status-converted/20",
      closed:
        "bg-status-no-show/10 text-status-no-show border border-status-no-show/20",
      website:
        "bg-status-scheduled/10 text-status-scheduled border border-status-scheduled/20",
      referral:
        "bg-primary-light/10 text-primary-light border border-primary-light/20",
      cold_call:
        "bg-status-not-interested/10 text-status-not-interested border border-status-not-interested/20",
    };
    return (
      colors[key] ||
      "bg-status-not-interested/10 text-status-not-interested border border-status-not-interested/20"
    );
  };

  // Collapse all sections toggle
  const toggleAllSections = () => {
    const allOpen =
      isMessagesOpen && isStatusOpen && isCategoryOpen && isFollowUpOpen && isTypeOpen && isPriorityOpen && isReadStatusOpen;
    const newState = !allOpen;
    setIsMessagesOpen(newState);
    setIsStatusOpen(newState);
    setIsCategoryOpen(newState);
    setIsFollowUpOpen(newState);
    setIsTypeOpen(newState);
    setIsPriorityOpen(newState);
    setIsReadStatusOpen(newState);
  };

  // Auto-collapse filters that are expanded by default when they have no items
  useEffect(() => {
    const hasPriorityItems = priorityCounts.high_priority > 0 ||
                             priorityCounts.standard > 0 ||
                             priorityCounts.routine > 0 ||
                             priorityCounts.urgent > 0;
    if (!isSheet) setIsPriorityOpen(hasPriorityItems);
  }, [priorityCounts, isSheet]);

  useEffect(() => {
    const hasStatusItems = leadStatusData.some(s => s.value > 0);
    if (!isSheet) setIsStatusOpen(hasStatusItems);
  }, [leadStatusData, isSheet]);

  // Calculate if any filters are active
  // Get active status count
  const activeStatusCount = leadStatusData.filter((s) => s.value > 0).length;
  const activeTypeCount = leadTypeData.filter(
    (t) => t.value > 0 && t.name !== ""
  ).length;
  const totalTypeCount = leadTypeData.reduce((sum, type) => sum + type.value, 0);
  const totalLeadsCount = leadStatusData.reduce((sum, s) => sum + s.value, 0);
  const totalCategoryCount = Object.values(categoryCounts).reduce((sum, v) => sum + v, 0);
  const activeCategoryCount = Object.values(categoryCounts).filter((v) => v > 0).length;

  // Active value shown beside a section title in the sheet.
  const sheetValue = (text) =>
    isSheet && text ? (
      <span className="truncate text-xs font-normal text-primary">{text}</span>
    ) : null;

  return (
    <div
      className={
        isSheet
          ? "w-full bg-white"
          : "min-w-[240px] max-w-[300px] bg-white rounded-lg border border-border shadow-sm p-4 self-start"
      }
    >
      {/* Header with Collapse All */}
      <div className={isSheet ? "hidden" : "flex justify-between items-center mb-4"}>
        <h1 className="text-lg font-semibold text-text-primary">Filters</h1>
        <button
          onClick={toggleAllSections}
          className="text-xs text-primary hover:underline font-medium"
        >
          {isMessagesOpen ||
          isStatusOpen ||
          isCategoryOpen ||
          isFollowUpOpen ||
          isTypeOpen ||
          isPriorityOpen ||
          isReadStatusOpen
            ? "Collapse All"
            : "Expand All"}
        </button>
      </div>

      {/* Needs Callback worklist — missed calls + answering-machine messages */}
      {!isSheet && (needsCallbackCount > 0 || selectedNeedsCallback) && (
      <button
        onClick={() =>
          onNeedsCallbackChange && onNeedsCallbackChange(!selectedNeedsCallback)
        }
        className={`w-full mb-4 flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition border ${
          selectedNeedsCallback
            ? "bg-red-600 text-white border-red-600"
            : "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
        }`}
      >
        <div className="flex items-center gap-2">
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
          <span>Needs Callback</span>
        </div>
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
            selectedNeedsCallback
              ? "bg-white/20 text-white"
              : "bg-red-600 text-white"
          }`}
        >
          {needsCallbackCount}
        </span>
      </button>
      )}

      {/* Patients whose latest text no staff has answered */}
      {!isSheet && messagingEnabled && (waitingReplyCount > 0 || selectedWaitingReply) && (
        <div className="mb-4">
          <button
            onClick={() =>
              onWaitingReplyChange && onWaitingReplyChange(!selectedWaitingReply)
            }
            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition border ${
              selectedWaitingReply
                ? "bg-amber-600 text-white border-amber-600"
                : "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100"
            }`}
          >
            <span>Texted, waiting for reply</span>
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                selectedWaitingReply
                  ? "bg-white/20 text-white"
                  : "bg-amber-600 text-white"
              }`}
            >
              {waitingReplyCount}
            </span>
          </button>
          <p className="mt-1 text-xs text-gray-500">
            Tap Contacted when you have called or texted back
          </p>
        </div>
      )}

      {/* Content Area */}
      <div className="pr-1">
        {/* Messages Section */}
        {messagingEnabled && (
          <>
            <div className="mb-4">
              <button
                className="w-full flex items-center justify-between text-text-primary font-medium text-sm mb-3 hover:text-primary transition sticky top-0 bg-white py-1 z-10"
                onClick={() => setIsMessagesOpen(!isMessagesOpen)}
              >
                <div className="flex items-center gap-2">
                  <svg
                    className="h-4 w-4 text-primary"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  <span>Messages</span>
                  {sheetValue(selectedUnreadMessages ? "Unread" : "")}
                  {unreadMessagesCount > 0 && (
                    <span className="text-xs text-text-secondary">
                      ({unreadMessagesCount} unread)
                    </span>
                  )}
                </div>
                <svg
                  className={`h-3 w-3 transform transition-transform ${
                    isMessagesOpen ? "rotate-180" : ""
                  }`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                </svg>
              </button>
              {isMessagesOpen && (
                <div className="space-y-1.5 pl-1">
                  <button
                    onClick={() =>
                      onUnreadMessagesChange && onUnreadMessagesChange(false)
                    }
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition flex justify-between items-center ${
                      !selectedUnreadMessages
                        ? "bg-accent-teal-subtle text-primary"
                        : "text-text-secondary hover:bg-accent-teal-subtle/30"
                    }`}
                  >
                    <span>All Messages</span>
                    {!selectedUnreadMessages && (
                      <div className="w-2 h-2 rounded-full bg-primary"></div>
                    )}
                  </button>
                  <button
                    onClick={() =>
                      onUnreadMessagesChange && onUnreadMessagesChange(true)
                    }
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition flex justify-between items-center ${
                      selectedUnreadMessages
                        ? "bg-accent-teal-subtle text-primary"
                        : "text-text-secondary hover:bg-accent-teal-subtle/30"
                    }`}
                  >
                    <span>Unread Messages</span>
                    <div className="flex items-center gap-2">
                      {unreadMessagesCount > 0 && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-primary text-white">
                          {unreadMessagesCount}
                        </span>
                      )}
                      {selectedUnreadMessages && (
                        <div className="w-2 h-2 rounded-full bg-primary"></div>
                      )}
                    </div>
                  </button>
                </div>
              )}
            </div>
            <div className="my-4 border-t border-border"></div>
          </>
        )}

        {/* Lead Status Section */}
        <div className="mb-4">
          <button
            className="w-full flex items-center justify-between text-text-primary font-medium text-sm mb-3 hover:text-primary transition sticky top-0 bg-white py-1 z-10"
            onClick={() => setIsStatusOpen(!isStatusOpen)}
          >
            <div className="flex items-center gap-2">
              <svg
                className="h-4 w-4 text-primary"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
              </svg>
              <span>Status</span>
              {sheetValue(leadStatusData.find((x) => x.key === selectedStatus)?.label || selectedStatus)}
              <span className="text-xs text-text-secondary">
                ({activeStatusCount})
              </span>
            </div>
            <svg
              className={`h-3 w-3 transform transition-transform ${
                isStatusOpen ? "rotate-180" : ""
              }`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
            </svg>
          </button>
          {isStatusOpen && (
            <div className="space-y-1.5 pl-1">
              <button
                onClick={() =>
                  onFilterChange({ status: "", type: selectedType })
                }
                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition flex justify-between items-center ${
                  selectedStatus === ""
                    ? "bg-accent-teal-subtle text-primary"
                    : "text-text-secondary hover:bg-accent-teal-subtle/30"
                }`}
              >
                <span className="flex-1">All Statuses</span>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      selectedStatus === ""
                        ? "bg-primary/10 text-primary border border-primary/20"
                        : "bg-text-secondary/10 text-text-secondary border border-text-secondary/20"
                    }`}
                  >
                    {leadStatusData.reduce(
                      (sum, status) => sum + status.value,
                      0,
                    )}
                  </span>
                  {selectedStatus === "" && (
                    <div className="w-2 h-2 rounded-full bg-primary"></div>
                  )}
                </div>
              </button>
              {leadStatusData
                .filter((status) => status.value > 0)
                .map((status) => (
                  <button
                    key={status.key}
                    onClick={() =>
                      onFilterChange({ status: status.key, type: selectedType })
                    }
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition flex justify-between items-center group ${
                      selectedStatus === status.key
                        ? "bg-accent-teal-subtle text-primary"
                        : "text-text-secondary hover:bg-accent-teal-subtle/30"
                    }`}
                  >
                    <span className="flex-1">{status.label}</span>
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                          status.key,
                        )}`}
                      >
                        {status.value}
                      </span>
                      {selectedStatus === status.key && (
                        <div className="w-2 h-2 rounded-full bg-primary"></div>
                      )}
                    </div>
                  </button>
                ))}
            </div>
          )}
        </div>

        <div className="my-4 border-t border-border"></div>

        {/* Lead Category Section */}
        <div className="mb-4">
          <button
            className="w-full flex items-center justify-between text-text-primary font-medium text-sm mb-3 hover:text-primary transition sticky top-0 bg-white py-1 z-10"
            onClick={() => setIsCategoryOpen(!isCategoryOpen)}
          >
            <div className="flex items-center gap-2">
              <svg
                className="h-4 w-4 text-primary"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z"
                  clipRule="evenodd"
                />
              </svg>
              <span>Category</span>
              {sheetValue(LEAD_CATEGORIES.find((c) => c.key === selectedCategory)?.label)}
              <span className="text-xs text-text-secondary">
                ({activeCategoryCount})
              </span>
            </div>
            <svg
              className={`h-3 w-3 transform transition-transform ${isCategoryOpen ? "rotate-180" : ""}`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
            </svg>
          </button>
          {isCategoryOpen && (
            <div className="space-y-1.5 pl-1">
              <button
                onClick={() =>
                  onCategoryFilterChange && onCategoryFilterChange("")
                }
                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition flex justify-between items-center ${
                  selectedCategory === ""
                    ? "bg-accent-teal-subtle text-primary"
                    : "text-text-secondary hover:bg-accent-teal-subtle/30"
                }`}
              >
                <span className="flex-1">All Categories</span>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      selectedCategory === ""
                        ? "bg-primary/10 text-primary border border-primary/20"
                        : "bg-text-secondary/10 text-text-secondary border border-text-secondary/20"
                    }`}
                  >
                    {totalCategoryCount}
                  </span>
                  {selectedCategory === "" && (
                    <div className="w-2 h-2 rounded-full bg-primary"></div>
                  )}
                </div>
              </button>
              {LEAD_CATEGORIES.map((cat) => {
                const colorMap = {
                  prospect: "bg-teal-100 text-teal-800 border border-teal-300",
                  existing_patient:
                    "bg-sky-100 text-sky-800 border border-sky-300",
                  not_applicable:
                    "bg-rose-100 text-rose-700 border border-rose-300",
                };
                return (
                  <button
                    key={cat.key}
                    onClick={() =>
                      onCategoryFilterChange && onCategoryFilterChange(cat.key)
                    }
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition flex justify-between items-center ${
                      selectedCategory === cat.key
                        ? colorMap[cat.key] ||
                          "bg-accent-teal-subtle text-primary"
                        : "text-text-secondary hover:bg-accent-teal-subtle/30"
                    }`}
                  >
                    <span className="flex-1">{cat.label}</span>
                    <div className="flex items-center gap-2">
                      {(categoryCounts[cat.key] || 0) > 0 && (
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            colorMap[cat.key] ||
                            "bg-text-secondary/10 text-text-secondary border border-text-secondary/20"
                          }`}
                        >
                          {categoryCounts[cat.key]}
                        </span>
                      )}
                      {selectedCategory === cat.key && (
                        <div className="w-2 h-2 rounded-full bg-current"></div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="my-4 border-t border-border"></div>

        {/* Read Status Section */}
        <div className="mb-4">
          <button
            className="w-full flex items-center justify-between text-text-primary font-medium text-sm mb-3 hover:text-primary transition sticky top-0 bg-white py-1 z-10"
            onClick={() => setIsReadStatusOpen(!isReadStatusOpen)}
          >
            <div className="flex items-center gap-2">
              <svg
                className="h-4 w-4 text-blue-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path
                  fillRule="evenodd"
                  d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                  clipRule="evenodd"
                />
              </svg>
              <span>Read Status</span>
              {sheetValue(READ_STATUS_OPTIONS.find((o) => o.key === selectedReadStatus && o.key)?.label)}
              {unreadLeadsCount > 0 && (
                <span className="text-xs text-text-secondary">
                  ({unreadLeadsCount} unread)
                </span>
              )}
            </div>
            <svg
              className={`h-3 w-3 transform transition-transform ${
                isReadStatusOpen ? "rotate-180" : ""
              }`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
            </svg>
          </button>
          {isReadStatusOpen && (
            <div className="space-y-1.5 pl-1">
              {READ_STATUS_OPTIONS.map((option) => (
                <button
                  key={option.key}
                  onClick={() =>
                    onReadStatusFilterChange &&
                    onReadStatusFilterChange(option.key)
                  }
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition flex justify-between items-center ${
                    selectedReadStatus === option.key
                      ? option.key === ""
                        ? "bg-accent-teal-subtle text-primary"
                        : `${option.color}`
                      : "text-text-secondary hover:bg-accent-teal-subtle/30"
                  }`}
                >
                  <span className="flex-1">{option.label}</span>
                  <div className="flex items-center gap-2">
                    {option.key === "" && totalLeadsCount > 0 && (
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          selectedReadStatus === ""
                            ? "bg-primary/10 text-primary border border-primary/20"
                            : "bg-text-secondary/10 text-text-secondary border border-text-secondary/20"
                        }`}
                      >
                        {totalLeadsCount}
                      </span>
                    )}
                    {option.key === "false" && unreadLeadsCount > 0 && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-500 text-white">
                        {unreadLeadsCount}
                      </span>
                    )}
                    {option.key === "true" && totalLeadsCount - unreadLeadsCount > 0 && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-border">
                        {totalLeadsCount - unreadLeadsCount}
                      </span>
                    )}
                    {selectedReadStatus === option.key && (
                      <div className="w-2 h-2 rounded-full bg-current"></div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="my-4 border-t border-border"></div>

        {/* AI Priority Section */}
        <div className="mb-4">
          <button
            className="w-full flex items-center justify-between text-text-primary font-medium text-sm mb-3 hover:text-primary transition sticky top-0 bg-white py-1 z-10"
            onClick={() => setIsPriorityOpen(!isPriorityOpen)}
          >
            <div className="flex items-center gap-2">
              <svg
                className="h-4 w-4 text-primary"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                  clipRule="evenodd"
                />
              </svg>
              <span>AI Priority</span>
              {sheetValue([PRIORITY_OPTIONS.find((o) => o.key === selectedPriority && o.key)?.label, selectedUrgentOnly && "Urgent", AI_NEXT_ACTIONS.find((a) => a.key === selectedNextAction)?.label].filter(Boolean).join(", "))}
            </div>
            <svg
              className={`h-3 w-3 transform transition-transform ${
                isPriorityOpen ? "rotate-180" : ""
              }`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
            </svg>
          </button>
          {isPriorityOpen && (
            <div className="space-y-1.5 pl-1">
              {PRIORITY_OPTIONS.map((priority) => (
                <button
                  key={priority.key}
                  onClick={() =>
                    onPriorityFilterChange &&
                    onPriorityFilterChange(priority.key)
                  }
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition flex justify-between items-center ${
                    selectedPriority === priority.key
                      ? priority.key === ""
                        ? "bg-accent-teal-subtle text-primary"
                        : `${priority.color}`
                      : "text-text-secondary hover:bg-accent-teal-subtle/30"
                  }`}
                >
                  <span>{priority.label}</span>
                  <div className="flex items-center gap-2">
                    {priority.countKey &&
                      priorityCounts[priority.countKey] > 0 && (
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${priority.color}`}
                        >
                          {priorityCounts[priority.countKey]}
                        </span>
                      )}
                    {selectedPriority === priority.key && (
                      <div className="w-2 h-2 rounded-full bg-current"></div>
                    )}
                  </div>
                </button>
              ))}
              {/* Urgent Only Toggle */}
              <div className="mt-2 pt-2 border-t border-gray-100">
                <button
                  onClick={() =>
                    onUrgentOnlyChange &&
                    onUrgentOnlyChange(!selectedUrgentOnly)
                  }
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition flex justify-between items-center ${
                    selectedUrgentOnly
                      ? "bg-red-100 text-red-800 border border-red-300"
                      : "text-text-secondary hover:bg-accent-teal-subtle/30"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span>⚠</span>
                    <span>Urgent Only</span>
                  </span>
                  <div className="flex items-center gap-2">
                    {priorityCounts.urgent > 0 && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-300">
                        {priorityCounts.urgent}
                      </span>
                    )}
                    {selectedUrgentOnly && (
                      <div className="w-2 h-2 rounded-full bg-current"></div>
                    )}
                  </div>
                </button>
              </div>

              {/* Next action — the AI's read of what this lead needs, so the
                  front desk can pull up "everyone needing a call back". */}
              <div className="mt-2 pt-2 border-t border-gray-100">
                <label
                  htmlFor="ai-next-action-filter"
                  className="block text-xs text-text-secondary mb-1.5 px-1"
                >
                  Next action
                </label>
                <select
                  id="ai-next-action-filter"
                  value={selectedNextAction || ""}
                  onChange={(e) =>
                    onNextActionFilterChange &&
                    onNextActionFilterChange(e.target.value)
                  }
                  className={`w-full px-3 py-2 rounded-lg text-sm font-medium border transition ${
                    selectedNextAction
                      ? "bg-accent-teal-subtle text-primary border-primary/30"
                      : "text-text-secondary border-border hover:bg-accent-teal-subtle/30"
                  }`}
                >
                  <option value="">Any next action</option>
                  {AI_NEXT_ACTIONS.map((a) => (
                    <option key={a.key} value={a.key}>
                      {a.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        <div className="my-4 border-t border-border"></div>

        {/* Follow-Up Status Section */}
        <div className="mb-4">
          <button
            className="w-full flex items-center justify-between text-text-primary font-medium text-sm mb-3 hover:text-primary transition sticky top-0 bg-white py-1 z-10"
            onClick={() => setIsFollowUpOpen(!isFollowUpOpen)}
          >
            <div className="flex items-center gap-2">
              <svg
                className="h-4 w-4 text-status-contacted"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                  clipRule="evenodd"
                />
              </svg>
              <span>Reminders</span>
              {sheetValue(FOLLOW_UP_STATUSES.find((o) => o.key === selectedFollowUpStatus && o.key)?.label)}
            </div>
            <svg
              className={`h-3 w-3 transform transition-transform ${
                isFollowUpOpen ? "rotate-180" : ""
              }`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
            </svg>
          </button>
          {isFollowUpOpen && (
            <div className="space-y-1.5 pl-1">
              {FOLLOW_UP_STATUSES.map((followUpStatus) => (
                <button
                  key={followUpStatus.key}
                  onClick={() =>
                    onFollowUpFilterChange &&
                    onFollowUpFilterChange(followUpStatus.key)
                  }
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition flex justify-between items-center ${
                    selectedFollowUpStatus === followUpStatus.key
                      ? followUpStatus.key === ""
                        ? "bg-accent-teal-subtle text-primary"
                        : `${followUpStatus.color}`
                      : "text-text-secondary hover:bg-accent-teal-subtle/30"
                  }`}
                >
                  <span className="flex-1">{followUpStatus.label}</span>
                  <div className="flex items-center gap-2">
                    {followUpStatus.key !== "" && (followUpCounts[followUpStatus.key] || 0) > 0 && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${followUpStatus.color}`}>
                        {followUpCounts[followUpStatus.key]}
                      </span>
                    )}
                    {selectedFollowUpStatus === followUpStatus.key && (
                      <div className="w-2 h-2 rounded-full bg-current"></div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="my-4 border-t border-border"></div>

        {/* Lead Types Section */}
        <div className="mb-4">
          <button
            className="w-full flex items-center justify-between text-text-primary font-medium text-sm mb-3 hover:text-primary transition sticky top-0 bg-white py-1 z-10"
            onClick={() => setIsTypeOpen(!isTypeOpen)}
          >
            <div className="flex items-center gap-2">
              <svg
                className="h-4 w-4 text-primary"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
              </svg>
              <span>Lead Types</span>
              {sheetValue(selectedType)}
              <span className="text-xs text-text-secondary">
                ({activeTypeCount})
              </span>
            </div>
            <svg
              className={`h-3 w-3 transform transition-transform ${
                isTypeOpen ? "rotate-180" : ""
              }`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
            </svg>
          </button>
          {isTypeOpen && (
            <div className="space-y-1.5 pl-1">
              <button
                onClick={() =>
                  onFilterChange({ status: selectedStatus, type: "" })
                }
                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition flex justify-between items-center ${
                  selectedType === ""
                    ? "bg-accent-teal-subtle text-primary"
                    : "text-text-secondary hover:bg-accent-teal-subtle/30"
                }`}
              >
                <span>All Types</span>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      selectedType === ""
                        ? "bg-primary/10 text-primary border border-primary/20"
                        : "bg-text-secondary/10 text-text-secondary border border-text-secondary/20"
                    }`}
                  >
                    {totalTypeCount}
                  </span>
                  {selectedType === "" && (
                    <div className="w-2 h-2 rounded-full bg-primary"></div>
                  )}
                </div>
              </button>
              {leadTypeData
                .filter((type) => type.value > 0 && type.name !== "")
                .slice(0, 5)
                .map((type) => (
                  <button
                    key={type.name}
                    onClick={() =>
                      onFilterChange({
                        status: selectedStatus,
                        type: type.name,
                      })
                    }
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition flex justify-between items-center ${
                      selectedType === type.name
                        ? "bg-accent-teal-subtle text-primary"
                        : "text-text-secondary hover:bg-accent-teal-subtle/30"
                    }`}
                  >
                    <span className="flex-1">
                      {type.name
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                    </span>
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                          type.name,
                        )}`}
                      >
                        {type.value}
                      </span>
                      {selectedType === type.name && (
                        <div className="w-2 h-2 rounded-full bg-primary"></div>
                      )}
                    </div>
                  </button>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadListSidebar;
